/**
 * Simple polling service for telemetry updates
 * Replaces WebSocket functionality with direct API polling
 */

class PollingService {
  constructor() {
    this.apiClient = null;
    this.pollingEnabled = false;
    this.pollingInterval = 5000; // 5 seconds
    this.pollingTimer = null;
    this.subscriptions = new Map(); // canopyId -> handler
    this.lastTelemetryData = new Map(); // Track last known telemetry for each canopy
  }

  /**
   * Initialize the polling service with an API client
   * @param {Object} apiClient - The API client instance
   */
  initialize(apiClient) {
    this.apiClient = apiClient;
    console.log('[PollingService] Initialized with API client');
  }

  /**
   * Start polling for telemetry updates
   */
  startPolling() {
    if (this.pollingEnabled || !this.apiClient) {
      return;
    }
    
    console.log('[PollingService] Starting polling service');
    this.pollingEnabled = true;
    this.pollTelemetry();
  }

  /**
   * Stop polling
   */
  stopPolling() {
    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
    this.pollingEnabled = false;
    console.log('[PollingService] Stopped polling service');
  }

  /**
   * Poll for telemetry updates
   */
  async pollTelemetry() {
    if (!this.pollingEnabled || !this.apiClient) {
      return;
    }

    try {
      // Poll each subscribed canopy
      for (const [canopyId, handler] of this.subscriptions) {
        try {
          const response = await this.apiClient.getLatestTelemetry(canopyId);
          if (response && response.data) {
            const telemetryData = response.data;
            const lastData = this.lastTelemetryData.get(canopyId);
            
            // Check if we have new data
            if (!lastData || lastData.message_id !== telemetryData.message_id) {
              console.log('[PollingService] New telemetry data for canopy', canopyId);
              this.lastTelemetryData.set(canopyId, telemetryData);
              
              // Notify handler
              try {
                handler(telemetryData);
              } catch (error) {
                console.error('Error in telemetry update handler:', error);
              }
            }
          }
        } catch (error) {
          console.error('[PollingService] Error polling telemetry for canopy', canopyId, error);
        }
      }
    } catch (error) {
      console.error('[PollingService] Error in polling cycle:', error);
    }

    // Schedule next poll
    if (this.pollingEnabled) {
      this.pollingTimer = setTimeout(() => {
        this.pollTelemetry();
      }, this.pollingInterval);
    }
  }

  /**
   * Subscribe to telemetry updates for specific canopy IDs
   * @param {string|Array} canopyIds - Canopy ID(s) to subscribe to
   * @param {function} handler - The function to call when a message is received for these canopies
   */
  subscribe(canopyIds, handler) {
    console.log('[PollingService] subscribe() called for:', canopyIds);
    // Ensure canopyIds is always an array to handle both single and multiple subscriptions
    const ids = Array.isArray(canopyIds) ? canopyIds : [canopyIds];

    ids.forEach(canopyId => {
      this.subscriptions.set(canopyId, handler);
    });
    
    // Start polling when subscribing
    this.startPolling();
  }

  /**
   * Unsubscribe from telemetry updates for specific canopy IDs
   * @param {string|Array} canopyIds - Canopy ID(s) to unsubscribe from
   */
  unsubscribe(canopyIds) {
    const ids = Array.isArray(canopyIds) ? canopyIds : [canopyIds];
    console.log('[PollingService] unsubscribe() called for:', ids);
    ids.forEach(id => this.subscriptions.delete(id));
    
    // Stop polling if no more subscriptions
    if (this.subscriptions.size === 0) {
      this.stopPolling();
    }
  }

  /**
   * Get current subscriptions
   * @returns {Set} - Set of subscribed canopy IDs
   */
  getSubscriptions() {
    return new Set(this.subscriptions.keys());
  }
}

// Create singleton instance
const pollingService = new PollingService();

export default pollingService; 